/**
 * Transfer wizard entity bootstrap and company/property selection handlers.
 */
import { useEffect, type MutableRefObject } from 'react'
import { showApiError } from '@/lib/utils/notifications'
import type { TransferWizardFormValues } from '../types'
import * as transferApi from '../api/transferApi'
import {
  buildTransferFormPatchFromEntity,
  defaultTransferFormValues,
  resetTransferFormFromProperty,
} from '../utils/transferFormUtils'
import {
  companyTransferGetResponseValid,
  propertyTransferGetResponseValid,
} from '../utils/transferPropertyCompanyWizard'
import type { TransferProcessWorkflowDeps } from './transferWorkflowTypes'

type LoaderApi = {
  loadProperty: () => Promise<void>
  loadTransferProgress: () => Promise<void>
  loadNewStaffs: () => Promise<void>
  loadResidents: () => Promise<void>
  loadApplications: () => Promise<void>
  loadListedManagement: () => Promise<void>
}

export function useTransferProcessEntityInit(
  { routing, dialogs, form, core, lookups }: TransferProcessWorkflowDeps,
  loaders: LoaderApi,
  firsttimeRef: MutableRefObject<boolean>,
) {
  const { fragment, entityId, newCompanyIdFromRoute } = routing
  const { setConfirmPaymentClone, setConfirmInactiveListed } = dialogs
  const { reset, setValue, getValues, trigger, setValueValidated } = form
  const { loadProperty } = lookups
  const {
    setProcessing,
    setEntityLoadStatus,
    setPropertyData,
    propertyData,
    progressData,
    companyList,
    setCompanyList,
    setCompanyListOld,
    setLoadNewCompany,
    propertyList,
    setNewPropertyLabel,
  } = core

  const { loadTransferProgress, loadNewStaffs, loadResidents, loadApplications, loadListedManagement } =
    loaders

  function applySetPropertyInfo() {
    if (!propertyData) return
    const propData = { ...propertyData }
    delete (propData as { fk_company_id?: unknown }).fk_company_id
    const patch = buildTransferFormPatchFromEntity(propData)
    ;(Object.keys(patch) as (keyof TransferWizardFormValues)[]).forEach((k) => {
      if (k === 'newpropertyinfo') return
      if (k in defaultTransferFormValues() && patch[k] !== undefined) {
        setValue(k, patch[k] as never)
      }
    })
    void trigger()
  }

  async function maybeInactiveListed() {
    if (getValues('listed') !== '1') {
      setConfirmInactiveListed(true)
    }
  }

  async function handleCompanySelected(companyId: string, skipGate?: boolean) {
    if (!companyId) return
    if (fragment === 'transfer_company') {
      setProcessing(true)
      try {
        await loadProperty()
        const res = await transferApi.getCompanyById(companyId)
        setLoadNewCompany(res)
        const newData = { ...res }
        delete (newData as { company_name?: unknown }).company_name
        const patch = buildTransferFormPatchFromEntity(newData as Record<string, unknown>)
        ;(Object.keys(patch) as (keyof TransferWizardFormValues)[]).forEach((k) => {
          if (k === 'newpropertyinfo') return
          if (k in defaultTransferFormValues() && patch[k] !== undefined) {
            setValue(k, patch[k] as never)
          }
        })
        /** Angular `companyPropertiesList()` → `loadNewStaffs()` for destination domain display + staff context. */
        await loadNewStaffs()
        void trigger()
      } catch (e) {
        showApiError(e)
      } finally {
        setProcessing(false)
      }
      return
    }
    const gate =
      skipGate ||
      !(progressData.propertyinfo?.info && firsttimeRef.current) ||
      !firsttimeRef.current
    if (!gate && !skipGate) {
      firsttimeRef.current = false
    }
    if (!companyId || (!skipGate && progressData.propertyinfo?.info && firsttimeRef.current)) {
      /* Angular skips until firsttime false */
    }
    setProcessing(true)
    try {
      const response = await transferApi.getCompanyById(companyId)
      setLoadNewCompany(response)
      if (newCompanyIdFromRoute && !companyList.length) {
        setCompanyList([
          { value: newCompanyIdFromRoute, label: String(response.company_name ?? '') },
        ])
        setValueValidated('fk_company_id', newCompanyIdFromRoute)
      }
      if (String(response.payment_for_property) === '1') {
        setConfirmPaymentClone({ open: true, companyPayload: response })
        return
      }
      applySetPropertyInfo()
      await maybeInactiveListed()
      if (fragment === 'transfer_property') await loadNewStaffs()
      void trigger()
    } catch (e) {
      showApiError(e)
    } finally {
      setProcessing(false)
    }
  }

  function updateStaffandResident(propertyID: string) {
    const found = propertyList.find((p) => p.value === propertyID)
    if (found) setNewPropertyLabel(found.label)
    if (fragment === 'merge_property') {
      void loadNewStaffs()
      void loadResidents()
      void loadApplications()
      void loadListedManagement()
    }
  }

  useEffect(() => {
    if (!fragment || !entityId) return
    setEntityLoadStatus('pending')
    void (async () => {
      setProcessing(true)
      try {
        if (fragment === 'transfer_company') {
          const res = await transferApi.getCompanyForTransfer(entityId)
          if (!companyTransferGetResponseValid(res, entityId)) {
            setPropertyData(null)
            setEntityLoadStatus('failed')
            return
          }
          setPropertyData(res)
          resetTransferFormFromProperty(reset, res, {
            fk_company_id: '',
            company_name: String(res.company_name ?? ''),
          })
          // Angular waits until Transfer To is selected (`companyPropertiesList`) before loading property list
        } else {
          const res = await transferApi.getPropertyForTransfer(entityId)
          if (!propertyTransferGetResponseValid(res, entityId)) {
            setPropertyData(null)
            setEntityLoadStatus('failed')
            return
          }
          setPropertyData(res)
          resetTransferFormFromProperty(reset, res, { fk_company_id: '' })
          const noRegex = /^-?\d+$/
          const pn = res.payee_name
          if (pn && pn !== 'LEASING' && typeof pn === 'string' && !noRegex.test(pn)) {
            setValue('other_payee_name', pn)
            setValue('payee_name', 'OTHER')
          }
          if (fragment === 'transfer_resident' || fragment === 'merge_property') {
            setValue('property_name', String(res.property_name ?? ''))
          }
          if (fragment === 'split_property') {
            const np = buildTransferFormPatchFromEntity(res).newpropertyinfo
            if (np) setValue('newpropertyinfo', np)
            if (pn && pn !== 'LEASING' && typeof pn === 'string' && !noRegex.test(pn)) {
              setValue('newpropertyinfo.other_payee_name', String(pn))
              setValue('newpropertyinfo.payee_name', 'OTHER')
            }
            setCompanyListOld([
              { value: String(res.fk_company_id), label: String(res.company_name ?? '') },
            ])
            setTimeout(() => setValueValidated('old_company_id', String(res.fk_company_id ?? '')), 100)
          }
          await loadListedManagement()
        }
        try {
          await loadTransferProgress()
          if (newCompanyIdFromRoute) {
            setValueValidated('fk_company_id', newCompanyIdFromRoute)
            await handleCompanySelected(newCompanyIdFromRoute, true)
          }
        } catch (e) {
          showApiError(e)
        }
        setEntityLoadStatus('ready')
      } catch (e) {
        setPropertyData(null)
        setEntityLoadStatus('failed')
        showApiError(e)
      } finally {
        setProcessing(false)
      }
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps -- initial load mirrors Angular Init
  }, [fragment, entityId])

  return {
    handleCompanySelected,
    applySetPropertyInfo,
    maybeInactiveListed,
    updateStaffandResident,
  }
}
